import React, { Suspense, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Header, ErrorDialog } from 'Components/Common';
import { Grid, Typography, Button } from '@material-ui/core';
import { Link, useHistory } from 'react-router-dom';
import { CancelIcon } from 'images/svg';
import { formattingDigitCurrency } from 'utils';
import useStyles from './styles';
import { useAtom } from 'jotai';
import {
  tipsAtom,
  ticketAtom,
  goToPayAtom,
  amountToPayAtom,
  shareNoteAtom,
  stepPaymentTicketAtom,
  activePersonalizedTipsAtom,
} from 'Atoms/Ticket';
import { staticDataAtom } from 'Atoms/Common';
import { useTranslation } from 'react-i18next';
import { Loader } from 'Components/UI';

const Calculator = () => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();

  const [ticket] = useAtom(ticketAtom);
  const [staticData] = useAtom(staticDataAtom);
  const [amountToPay] = useAtom(amountToPayAtom);
  const { id, order } = ticket || {};
  const [shareNote, setShareNote] = useAtom(shareNoteAtom);
  const shareNoteSelected = shareNote.selected;
  const { countryCode, labelCode, symbol } = staticData?.currency;
  const [totalIsAmount] = useState(
    shareNoteSelected === 'perAmount' ? true : false,
  );
  const [, setStepPaymentTicket] = useAtom(stepPaymentTicketAtom);
  const [, setGoToPay] = useAtom(goToPayAtom);
  const [number, setNumber] = useState(0);
  const elementCalculatorToDisplay = [1, 2, 3, 4, 5, 6, 7, 8, 9, ',', 0];
  const [error, setError] = useState('');
  const [activePersonalizedTips, setActivePersonalizedTips] = useAtom(
    activePersonalizedTipsAtom,
  );
  const [tips, setTips] = useAtom(tipsAtom);

  const tipsDisabled = ticket.tips && ticket.tips.enable === false
  
  const redirectUrl = activePersonalizedTips || tipsDisabled
    ? `/ticket/:${id}/pay/form`
    : `/ticket/:${id}/tips`;

  const handleClearTotal = (e) => {
    e.preventDefault();
    setNumber(0);
    setError('');
  };

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setNumber(amountToPay);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleNumber = (e, value) => {
    e.preventDefault();
    const numberString =
      number !== 0 && number.toString() ? number.toString() : '';
    const newValueString = value !== ',' ? value.toString() : '.';
    const newTotalString = `${numberString}${newValueString}`;
    const amountToPayFormatting = formattingDigitCurrency(
      amountToPay,
      countryCode,
      labelCode,
    );
    if (!!activePersonalizedTips) {
      setNumber(newTotalString);
      return;
    }
    if (!totalIsAmount || newTotalString <= amountToPay) {
      setNumber(newTotalString);
    }

    if (newTotalString > amountToPay && totalIsAmount) {
      return setError(
        `${t('error_share_per_amount_to_high')} ${amountToPayFormatting}`,
      );
    }
  };

  const handleTouchStart = () => {};

  const handleCloseError = (e) => {
    setError('');
  };

  const delayRedirect = (e, to) => {
    e.preventDefault();
    const parseTotalNumber = convertTotalStringToNumber(number);
    const value = parseTotalNumber;
    let sharePerPeople;
    let selected;

    if (!activePersonalizedTips) {
      selected = shareNote.selected;
      sharePerPeople = shareNoteSelected === 'perPeople' ? true : false;
      setShareNote({ selected, value });
      setStepPaymentTicket('checkout');
    }

    if (
      (value > 0.1 && !sharePerPeople) ||
      (sharePerPeople && sharePerPeopleTotal(value)) ||
      !!activePersonalizedTips
    ) {
      setTips(value);
      setGoToPay(true);
      setTimeout(() => history.push(to), 700);
    } else {
      setError(t('error_pay_zero'));
    }
  };

  const sharePerPeopleTotal = (value) => {
    const totalOrder = order?.totals?.amount;
    const newTotal = totalOrder / value;
    return newTotal > 0.1 ? newTotal : 0;
  };

  const convertTotalStringToNumber = () => {
    const parseTotalNumber = parseFloat(number);
    setNumber(parseTotalNumber);
    return parseTotalNumber;
  };

  return (
    <>
      <Header
        title={t('my_ticket')}
        actions={{
          prev: {
            label: t('cancel'),
            path: `${
              activePersonalizedTips
                ? `/ticket/:${id}/tips/`
                : `/ticket/:${id}/share/`
            }`,
            icon: 'return',
          },
        }}
      ></Header>
      {error && error.length && (
        <ErrorDialog
          title={t('warning')}
          content={error}
          handleCloseError={handleCloseError}
          handleClearTotal={handleClearTotal}
        />
      )}
      <Suspense
        fallBack={
          <div id="spinner">
            <Loader />
          </div>
        }
      >
        <Grid
          container
          className={classes.root}
          justifyContent="center"
          alignContent="center"
          alignItems="center"
        >
          <Grid item xs={11}>
            <Grid
              container
              justifyContent="center"
              alignContent="center"
              alignItems="center"
            >
              <Grid item xs={11} className={classes.totalContainer}>
                <Button onClick={handleClearTotal}>
                  <CancelIcon />
                </Button>
                <Typography className={classes.totalLabel}>
                  <span>
                    {number && number !== '.' && !!totalIsAmount
                      ? parseFloat(number).toFixed(2)
                      : number}
                  </span>
                  <span className={classes.currency}>
                    {totalIsAmount ? symbol : ''}
                  </span>
                </Typography>
              </Grid>
            </Grid>
            <Grid container justifyContent="center" alignItems="center">
              {elementCalculatorToDisplay &&
                elementCalculatorToDisplay.map((item, i) => {
                  const disableComma =
                    item === ',' &&
                    ((!activePersonalizedTips && !totalIsAmount) ||
                      (!activePersonalizedTips &&
                        !!totalIsAmount &&
                        number <= 0))
                      ? true
                      : false;
                  const disableZero = number === 0 && item === 0;

                  return (
                    <Grid
                      item
                      xs={4}
                      className={`${classes.inputButtonContainer} ${
                        disableComma || disableZero ? classes.disabled : ''
                      }`}
                      key={`calculator-${i}`}
                    >
                      {!disableComma && (
                        <button
                          data-testid="select-nb"
                          className={` ${classes.inputButtonNumber} ${classes.test}`}
                          disabled={disableComma || disableZero ? true : false}
                          onClick={(e) => handleNumber(e, item)}
                          onTouchStart={handleTouchStart()}
                        >
                          {item}
                        </button>
                      )}
                    </Grid>
                  );
                })}
              <Grid item xs={4} className={classes.ctaValidation}>
                <Link
                  to={{}}
                  className={
                    number == 0 || number === '.'
                      ? `${classes.hidePointerEvents} ${classes.disabled}`
                      : ''
                  }
                  onClick={(e) => delayRedirect(e, redirectUrl)}
                >
                  <Button className={classes.ctaValidationButton}>
                    <Typography color="secondary">{t('validate')}</Typography>
                  </Button>
                </Link>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Suspense>
    </>
  );
};

Calculator.propTypes = {};

export default Calculator;
